import React from 'react';
import { Link } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { Tooltip } from 'react-tooltip';

interface ReportItem {
    id: string;
    name: string;
    description: string;
    category: 'Loans' | 'Financial' | 'Operational' | 'Compliance';
    frequency: 'Daily' | 'Weekly' | 'Monthly' | 'Quarterly' | 'On Demand';
    lastGenerated?: string;
    format: 'PDF' | 'Excel' | 'CSV';
}

const Reports = () => {
    const columns = [
        {
            name: 'Report Name',
            selector: (row: ReportItem) => row.name,
            sortable: true,
            cell: (row: ReportItem) => (
                <div>
                    <div className="fw-bold">{row.name}</div>
                    <small className="text-muted">{row.description}</small>
                </div>
            ),
        },
        {
            name: 'Category',
            selector: (row: ReportItem) => row.category,
            sortable: true,
            cell: (row: ReportItem) => (
                <span className={`badge bg-${
                    row.category === 'Loans' ? 'primary' :
                    row.category === 'Financial' ? 'success' :
                    row.category === 'Operational' ? 'info' : 'warning'
                }`}>
                    {row.category}
                </span>
            ),
        },
        {
            name: 'Frequency',
            selector: (row: ReportItem) => row.frequency,
            sortable: true,
        },
        {
            name: 'Format',
            selector: (row: ReportItem) => row.format,
            cell: (row: ReportItem) => (
                <span className={`badge bg-${
                    row.format === 'PDF' ? 'danger' :
                    row.format === 'Excel' ? 'success' : 'info'
                }`}>
                    {row.format}
                </span>
            ),
        },
        {
            name: 'Last Generated',
            selector: (row: ReportItem) => row.lastGenerated || 'Never',
            sortable: true,
        },
        {
            name: 'Actions',
            cell: (row: ReportItem) => (
                <div className="d-flex gap-2">
                    <button 
                        className="btn btn-sm btn-primary"
                        data-tooltip-id={`generate-${row.id}`}
                    >
                        <i className="fa fa-file-export"></i>
                    </button>
                    <Tooltip id={`generate-${row.id}`} place="top" content="Generate report" />
                    
                    <button 
                        className="btn btn-sm btn-outline-secondary"
                        data-tooltip-id={`schedule-${row.id}`}
                    >
                        <i className="fa fa-clock"></i>
                    </button>
                    <Tooltip id={`schedule-${row.id}`} place="top" content="Schedule report" />
                </div>
            ),
        },
    ];

    const data: ReportItem[] = [
        {
            id: '1',
            name: 'Loan Portfolio Summary',
            description: 'Overview of all active loans, including status and performance metrics',
            category: 'Loans',
            frequency: 'Daily',
            lastGenerated: '2024-02-21 10:30',
            format: 'PDF'
        },
        {
            id: '2',
            name: 'Financial Performance',
            description: 'Detailed financial metrics and KPIs',
            category: 'Financial',
            frequency: 'Monthly',
            lastGenerated: '2024-02-01 09:15',
            format: 'Excel'
        },
        {
            id: '3',
            name: 'Compliance Report',
            description: 'Regulatory compliance status and risk assessment',
            category: 'Compliance',
            frequency: 'Quarterly',
            lastGenerated: '2024-01-01 14:20',
            format: 'PDF'
        },
        {
            id: '4',
            name: 'Operational Metrics',
            description: 'Processing times and operational efficiency indicators',
            category: 'Operational',
            frequency: 'Weekly',
            lastGenerated: '2024-02-19 16:45',
            format: 'Excel'
        }
    ];

    const reportStats = [
        { title: 'Available Reports', value: '24', icon: 'fa-file-alt', color: 'primary' },
        { title: 'Generated Today', value: '8', icon: 'fa-calendar-check', color: 'success' },
        { title: 'Scheduled', value: '12', icon: 'fa-clock', color: 'warning' },
        { title: 'Templates', value: '6', icon: 'fa-file-code', color: 'info' },
    ];

    return (
        <div>
            <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-3">
                <div className="container-fluid">
                    <div className="row g-3 mb-3 align-items-center">
                        <div className="col">
                            <ol className="breadcrumb bg-transparent mb-0">
                                <li className="breadcrumb-item"><Link className="text-secondary" to="/">Home</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">Reports</li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
                <div className="container-fluid">
                    {/* Report Stats */}
                    <div className="row g-3 mb-3 row-deck">
                        {reportStats.map((stat, index) => (
                            <div key={index} className="col-lg-3 col-md-6 col-sm-6">
                                <div className="card">
                                    <div className="card-body">
                                        <i className={`fa ${stat.icon} position-absolute top-0 end-0 mt-4 me-3 text-${stat.color}`} style={{ fontSize: '24px' }}></i>
                                        <div className="mb-2 text-uppercase">{stat.title}</div>
                                        <div><span className="h4">{stat.value}</span></div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Reports that run on live data */}
                    <div className="row mb-3">
                        <div className="col-md-6 col-xl-4">
                            <Link to="/reports/decline-reasons" className="card text-decoration-none h-100">
                                <div className="card-body d-flex align-items-center">
                                    <i className="fa fa-pie-chart fa-2x text-danger me-3"></i>
                                    <div>
                                        <div className="fw-bold text-body">Decline reasons</div>
                                        <small className="text-muted">Why business is lost: declines by reason and stage, with CSV</small>
                                    </div>
                                </div>
                            </Link>
                        </div>
                    </div>

                    {/* Reports List */}
                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-header">
                                    <h6 className="card-title mb-0">Available Reports</h6>
                                    <div className="dropdown morphing scale-left">
                                        <button className="btn btn-sm btn-primary">
                                            <i className="fa fa-plus me-2"></i>Create Template
                                        </button>
                                    </div>
                                </div>
                                <div className="card-body">
                                    <div className="table-responsive">
                                        <DataTable
                                            columns={columns}
                                            data={data}
                                            pagination
                                            highlightOnHover
                                            striped
                                            responsive
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Reports; 